import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { ExportOutlined, FormOutlined, LogoutOutlined, MenuOutlined } from '@ant-design/icons';
import { Button, Drawer, Menu, MenuProps, Modal } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { ReactSVG } from 'react-svg';
import { isMobileOnly } from 'react-device-detect';
import styles from './tabmenu.module.scss';
import logo_jre from '../../../../assets/images/logo_jre.svg';
import { useLoginStore } from '../../pages/login/login_store';
import { getCompany, getUserName, useSessionStore } from '../../../context/session';
import { Menus } from './menu_items';
import { StaticLinks } from '../../../constant/menulinks';
import { PermissionName } from '../../../valueobject/permissionName';

/**
 * タブメニュー・ヘッダ
 * @param height ヘッダの高さ
 * @returns 
 */
export const TitleMenu: React.FC<{ height: number }> = ({ height }) => {
  const [time, setTime] = useState(dayjs());
  const [showDrawer, setShowDrawer] = useState(false);
  const location = useLocation();
  const logout = useLoginStore((selector) => selector.logout);
  const { loginInfo, hasPermission } = useSessionStore((selector) => selector);
  const navigator = useNavigate();

  useEffect(() => {
    const interval = setInterval(() => {
      setTime(dayjs());
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  const onNavigateMenu = (key: string, reflesh?: boolean) => {
    setShowDrawer(false);
    if (reflesh) {
      navigator(key, { state: { refresh: true } });
    } else {
      navigator(key);
    }
  };

  const items = useMemo(() => {
    let menus: MenuProps['items'] = [];
    menus = Menus.filter((menu) => hasPermission(menu.permission, menu.readWrite, menu.allowedBusinessTypes)).map((menu) => {
      if (isMobileOnly && menu.key !== '/notification' && menu.key !== '/issue') {
        return null;
      }
      return {
        key: menu.key,
        label: menu.label,
        onClick: () => {
          onNavigateMenu(menu.key, menu.reflesh);
        },
        icon: menu.icon,
      };
    });
    menus.push({ type: 'divider' });
    if (StaticLinks.length > 0) {
      menus = menus.concat(StaticLinks.map((link) => {
        return {
          key: link.url,
          label: link.name,
          icon: <ExportOutlined />,
          onClick: () => {
            window.open(link.url, '_blank');
          },
        };
      }));
      menus.push({ type: 'divider' });
    }
    menus.push(
      {
        key: '/logout',
        label: 'ログアウト',
        onClick: () => {
          logout(navigator);
        },
        icon: <LogoutOutlined />,
      },
    );
    return menus;
  }, [loginInfo]);

  const [openRobotAllStop, setOpenRobotAllStop] = useState(false);
  const [openRobotAllStopOK, setOpenRobotAllStopOK] = useState(false);
  const onClickRobotAllStop = useMemo(() => {
    return (
      <Modal
        title=""
        centered
        open={openRobotAllStop}
        onOk={() => {
          setOpenRobotAllStop(false);
          setOpenRobotAllStopOK(true);
        }}
        onCancel={() => setOpenRobotAllStop(false)}
        width={500}
        closable={false}
        footer={[
          <Button
            key="1"
            onClick={() => {
              setOpenRobotAllStop(false);
              setOpenRobotAllStopOK(true);
            }}
          >
            はい
          </Button>,
          <Button
            key="0"
            type="primary"
            autoFocus={true}
            onClick={() => {
              setOpenRobotAllStop(false);
            }}
          >
            いいえ
          </Button>,
        ]}
      >
        <div>ロボットを一括停止します。よろしいですか？</div>
      </Modal>
    );
  }, [openRobotAllStop]);
  const onClickRobotAllStopOK = useMemo(() => {
    return (
      <Modal
        title=""
        centered
        open={openRobotAllStopOK}
        width={500}
        closable={false}
        footer={[
          <Button
            key="0"
            type="primary"
            onClick={() => {
              setOpenRobotAllStopOK(false);
            }}
          >
            OK
          </Button>,
        ]}
      >
        <div>すべてのロボットに停止指示を出しました</div>
      </Modal>
    );
  }, [openRobotAllStopOK]);

  const issueButton = useMemo(() => {
    if (hasPermission(PermissionName.ISSUE, true)) {
      return (
              <Button
                  size={'small'}
                  type="default"
                  onClick={() => {
                    navigator('./issue', { state: { refresh: true } });
                  }}
              >
                  臨時作業入力
              </Button>
      );
    }
    return <span></span>;
  }, [hasPermission, navigator]);

  return (
    <div style={{ height }} className={styles.root}>
      <div className={styles.menu_bg}>
        <MenuOutlined className={styles.menu_button} onClick={() => setShowDrawer(true)} />
      </div>
      <div className={styles.logos}>
        <div className={styles.logo}>
          <ReactSVG src={logo_jre} />
        </div>
      </div>
        <div className={styles.time}>
            <div className={styles.pcTablet}>
                {issueButton}
            </div>
            <div className={styles.nameArea}>
                <div>
                    {getCompany(loginInfo).companyName}&nbsp;
                    {getUserName(loginInfo)}&nbsp;
                </div>
                <div>
                    {time.format('YYYY年MM月DD日 HH時mm分ss秒')}
                </div>
            </div>
            <div className={styles.sp}>
                <Button
                    size={'large'}
                    type="default"
                    onClick={() => {
                      navigator('./issue', { state: { refresh: true } });
                    }}
                    icon={<FormOutlined/>}
                />
            </div>
        </div>
        <Drawer
            title="メニュー"
            placement={'left'}
            width={300}
            onClose={() => setShowDrawer(false)}
            open={showDrawer}
            styles={{ body: { padding: 0 } }}
        >
            <Menu selectedKeys={[location.pathname]} items={items} inlineIndent={0} mode={'vertical'} />
      </Drawer>
      {onClickRobotAllStop}
      {onClickRobotAllStopOK}
    </div>
  );
};
